/**
 * AttachmentUploader.jsx - Componente de Upload de Comprovantes
 * CG Business ERP - P3/P3.1
 * 
 * Features:
 * - Drag & Drop
 * - Upload múltiplo
 * - Preview de imagens
 * - PDF thumbnails (1ª página) com lazy loading
 * - Download de ficheiros
 * - Soft delete com auditoria
 * - Indicador de progresso
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { attachmentsAPI, getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Upload, Paperclip, File, FileText, Image, Trash2, Download,
  Eye, X, Check, AlertCircle, Loader2, Clock, User, Camera
} from 'lucide-react';
import { formatDateTime, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { PdfThumbnail } from './PdfThumbnail';
import { useLanguage } from '@/i18n';

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// =============================================================================
// ATTACHMENT CARD COMPONENT
// =============================================================================
function AuthenticatedAttachmentImage({ attachment, className = '', alt = '' }) {
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    let active = true;
    let objectUrl = '';

    const loadImage = async () => {
      try {
        const response = await attachmentsAPI.fetchPreviewBlob(attachment.id);
        objectUrl = URL.createObjectURL(response.data);
        if (active) {
          setImageUrl(objectUrl);
        }
      } catch {
        if (active) {
          setImageUrl('');
        }
      }
    };

    loadImage();

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [attachment.id]);

  if (!imageUrl) {
    return (
      <div className={cn('w-full h-full flex items-center justify-center bg-slate-100', className)}>
        <Image className="h-6 w-6 text-slate-400" />
      </div>
    );
  }

  return <img src={imageUrl} alt={alt} className={className} />;
}

function AttachmentCard({ attachment, onDelete, onPreview, onDownload, canDelete }) {
  const fileType = attachment.file_type;
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileType);
  const isPdf = fileType === 'pdf';
  
  // Determine icon based on file type
  let IconComponent = File;
  if (isImage) {
    IconComponent = Image;
  } else if (isPdf) {
    IconComponent = FileText;
  }
  
  return (
    <div 
      className={cn(
        "flex items-center gap-3 p-3 border rounded-lg bg-white hover:bg-slate-50 transition-colors",
        attachment.is_deleted && "opacity-50 bg-red-50"
      )}
      data-testid={`attachment-${attachment.id}`}
    >
      {/* Thumbnail or Icon */}
      <div className="flex-shrink-0">
        {isImage ? (
          <div 
            className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center overflow-hidden cursor-pointer"
            onClick={() => onPreview(attachment)}
            data-testid={`attachment-preview-trigger-${attachment.id}`}
          >
            <AuthenticatedAttachmentImage
              attachment={attachment}
              alt={attachment.original_filename}
              className="w-full h-full object-cover"
            />
          </div>
        ) : isPdf ? (
          <div 
            className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center overflow-hidden cursor-pointer"
            onClick={() => onPreview(attachment)}
            data-testid={`attachment-preview-trigger-${attachment.id}`}
          >
            <PdfThumbnail 
              pdfUrl={attachmentsAPI.getDownloadUrl(attachment.id)}
              fileSize={attachment.file_size}
              fileName={attachment.original_filename}
              className="w-full h-full"
            />
          </div>
        ) : (
          <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center">
            <IconComponent className="h-6 w-6 text-slate-500" />
          </div>
        )}
      </div>
      
      {/* File info */}
      <div className="flex-grow min-w-0">
        <p className="font-medium text-sm truncate" title={attachment.original_filename}>
          {attachment.original_filename}
        </p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{formatFileSize(attachment.file_size)}</span>
          <span>•</span>
          <span>{attachment.file_type?.toUpperCase()}</span>
          {attachment.uploaded_by_name && (
            <>
              <span>•</span>
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {attachment.uploaded_by_name}
              </span>
            </>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {(isImage || isPdf) && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPreview(attachment)}
            title="Pré-visualizar"
            data-testid={`attachment-preview-button-${attachment.id}`}
          >
            <Eye className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onDownload(attachment)}
          title="Descarregar"
          data-testid={`attachment-download-button-${attachment.id}`}
        >
          <Download className="h-4 w-4" />
        </Button>
        {canDelete && !attachment.is_deleted && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => onDelete(attachment)}
            title="Eliminar"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function AttachmentUploader({
  entityType,  // 'expense' or 'transfer'
  entityId,
  attachments = [],
  onAttachmentsChange,
  maxFiles = 10,
  maxSizeMB = 10,
  acceptedTypes = '.pdf,.jpg,.jpeg,.png,.gif,.webp',
  compact = false,
  readOnly = false,
  testIdPrefix = 'attachment-uploader',
}) {
  const { t } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const { hasRole } = useAuth();
  const canUpload = hasRole(['admin', 'store_manager', 'accountant']);
  const canDelete = hasRole(['admin', 'store_manager']);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const validateFile = useCallback((file) => {
    // Check size
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `Ficheiro muito grande. Máximo: ${maxSizeMB}MB`;
    }
    
    // Check type
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!acceptedTypes.includes(ext)) {
      return `Tipo não permitido: ${ext}`;
    }
    
    return null;
  }, [maxSizeMB, acceptedTypes]);

  const uploadFiles = useCallback(async (files) => {
    if (!entityId) {
      toast.error(t('attachmentUploader.saveBeforeUpload'));
      return;
    }
    
    if (files.length === 0) return;
    
    // Validate all files first
    const validFiles = [];
    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        toast.error(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    }
    
    if (validFiles.length === 0) return;
    
    // Check max files limit
    if (attachments.length + validFiles.length > maxFiles) {
      toast.error(t('attachmentUploader.maxFiles', { count: maxFiles }));
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('entity_type', entityType);
      formData.append('entity_id', entityId);
      
      if (validFiles.length === 1) {
        formData.append('file', validFiles[0]);
        const response = await attachmentsAPI.upload(formData);
        toast.success(t('attachmentUploader.fileUploaded'));
        if (onAttachmentsChange) {
          onAttachmentsChange([...attachments, response.data.attachment]);
        }
      } else {
        validFiles.forEach(file => formData.append('files', file));
        const response = await attachmentsAPI.uploadMultiple(formData);
        
        if (response.data.errors?.length > 0) {
          response.data.errors.forEach(err => toast.error(`${err.filename}: ${err.error}`));
        }
        
        if (response.data.attachments?.length > 0) {
          toast.success(t('attachmentUploader.filesUploaded', { count: response.data.attachments.length }));
          if (onAttachmentsChange) {
            onAttachmentsChange([...attachments, ...response.data.attachments]);
          }
        }
      }
    } catch (error) {
      toast.error(getErrorMessage(error, t('attachmentUploader.uploadError')));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [attachments, entityId, entityType, maxFiles, onAttachmentsChange, t, validateFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (readOnly || !canUpload) return;
    
    const files = Array.from(e.dataTransfer.files);
    uploadFiles(files);
  }, [readOnly, canUpload, uploadFiles]);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    uploadFiles(files);
    e.target.value = ''; // Reset input
  }, [uploadFiles]);

  const handlePreview = useCallback((attachment) => {
    setPreviewAttachment(attachment);
    setPreviewOpen(true);
  }, []);

  const handleDownload = useCallback(async (attachment) => {
    try {
      await attachmentsAPI.downloadAuthenticated(attachment.id, attachment.original_filename);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao descarregar anexo'));
    }
  }, []);

  const handleDeleteClick = useCallback((attachment) => {
    setAttachmentToDelete(attachment);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!attachmentToDelete) return;
    
    try {
      await attachmentsAPI.delete(attachmentToDelete.id, deleteReason || null);
      toast.success(t('attachmentUploader.deleted'));
      
      if (onAttachmentsChange) {
        onAttachmentsChange(attachments.filter(a => a.id !== attachmentToDelete.id));
      }
    } catch (error) {
      toast.error(getErrorMessage(error, t('attachmentUploader.deleteError')));
    } finally {
      setDeleteDialogOpen(false);
      setAttachmentToDelete(null);
      setDeleteReason('');
    }
  }, [attachmentToDelete, attachments, deleteReason, onAttachmentsChange, t]);

  useEffect(() => {
    if (!previewOpen || !previewAttachment) {
      setPreviewObjectUrl('');
      setPreviewLoading(false);
      return undefined;
    }

    let active = true;
    let objectUrl = '';

    const loadPreview = async () => {
      try {
        setPreviewLoading(true);
        const response = await attachmentsAPI.fetchPreviewBlob(previewAttachment.id);
        objectUrl = URL.createObjectURL(response.data);
        if (active) {
          setPreviewObjectUrl(objectUrl);
        }
      } catch (error) {
        if (active) {
          toast.error(getErrorMessage(error, 'Erro ao carregar a pré-visualização do anexo'));
          setPreviewObjectUrl('');
        }
      } finally {
        if (active) {
          setPreviewLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [previewAttachment, previewOpen, t]);

  // ==========================================================================
  // RENDER
  // ==========================================================================
  
  const activeAttachments = attachments.filter(a => !a.is_deleted);

  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      {/* Drop Zone */}
      {!readOnly && canUpload && (
        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
            dragActive ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300",
            uploading && "pointer-events-none opacity-50"
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={acceptedTypes}
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {uploading ? (
            <div className="space-y-2">
              <Loader2 className="h-8 w-8 mx-auto text-blue-500 animate-spin" />
              <p className="text-sm text-muted-foreground">{t('attachmentUploader.uploading')}</p>
              <Progress value={uploadProgress} className="w-48 mx-auto" />
            </div>
          ) : (
            <>
              <Upload className={cn(
                "h-8 w-8 mx-auto mb-2",
                dragActive ? "text-blue-500" : "text-slate-400"
              )} />
              <p className="text-sm font-medium">
                {dragActive ? t('attachmentUploader.dropHere') : t('attachmentUploader.dragOrClick')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('attachmentUploader.fileHint', { size: maxSizeMB, count: maxFiles })}
              </p>
            </>
          )}
        </div>
      )}
      {!readOnly && canUpload && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            onClick={() => cameraInputRef.current?.click()}
            data-testid={`${testIdPrefix}-camera-button`}
          >
            <Camera className="h-4 w-4" />
            {t('attachmentUploader.takePhoto')}
          </Button>
        </div>
      )}

      {/* Attachments List */}
      {activeAttachments.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              {t('attachmentUploader.attachmentsCount', { count: activeAttachments.length })}
            </Label>
          </div>
          
          <div className={cn("space-y-2", compact && "max-h-48 overflow-y-auto")}>
            {activeAttachments.map((attachment) => (
              <AttachmentCard
                key={attachment.id}
                attachment={attachment}
                onDelete={handleDeleteClick}
                onPreview={handlePreview}
                onDownload={handleDownload}
                canDelete={canDelete && !readOnly}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {activeAttachments.length === 0 && readOnly && (
        <div className="text-center py-6 text-muted-foreground">
          <Paperclip className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{t('attachmentUploader.noAttachments')}</p>
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{previewAttachment?.original_filename}</DialogTitle>
          </DialogHeader>
          {previewAttachment && (
            <div className="flex justify-center" data-testid={`attachment-preview-dialog-${previewAttachment.id}`}>
              {previewLoading ? (
                <div className="flex h-[50vh] items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : previewObjectUrl ? (
                ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(previewAttachment.file_type) ? (
                  <img
                    src={previewObjectUrl}
                    alt={previewAttachment.original_filename}
                    className="max-h-[70vh] object-contain"
                  />
                ) : (
                  <iframe
                    src={previewObjectUrl}
                    title={previewAttachment.original_filename}
                    className="h-[70vh] w-full rounded-lg border"
                  />
                )
              ) : (
                <div className="flex h-[50vh] items-center justify-center text-sm text-muted-foreground">
                  Não foi possível carregar a pré-visualização deste anexo.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => previewAttachment && handleDownload(previewAttachment)}
              data-testid={previewAttachment ? `attachment-dialog-download-button-${previewAttachment.id}` : 'attachment-dialog-download-button'}
            >
              <Download className="h-4 w-4 mr-2" />
              {t('actions.download')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('attachmentUploader.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('attachmentUploader.deleteDescription', { file: attachmentToDelete?.original_filename || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-sm">{t('attachmentUploader.reasonOptional')}</Label>
            <Textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder={t('attachmentUploader.reasonPlaceholder')}
              rows={2}
              className="mt-1"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
              {t('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
